import type { DocPackCatalog, DocPackReadingClass } from '@listening/doc-pack';
import {
  AlignLeft,
  AlignVerticalSpacing,
  Asterisk,
  BookStack,
  Erase,
  Eye,
  FilterList,
  Hashtag,
  HSquare,
  MediaImage,
  Notes,
  Quote,
  ShortcutSquare,
  SigmaFunction,
  Table,
  Undo,
} from 'iconoir-react';
import { cn } from '@listening/utils/react-helpers';
import { Button } from '@listening/frontend/components/radix/button';
import { Switch } from '@listening/frontend/components/radix/switch';
import {
  useDocPackReadingPreferencesActions,
  useDocPackShowLabelRegions,
} from '@listening/frontend/stores/doc-pack-reading-preferences-store';
import { useReadAlongMainContentView } from '@listening/frontend/stores/audio-preferences-store';
import {
  type DocPackReadingLabel,
  docPackReadingLabelHint,
  docPackReadingLabelName,
} from '@listening/frontend/doc-pack/reading-labels';
import { clientEnv } from '@/env';
import {
  useDocPack,
  useDocPackReadingLabelControls,
} from '@/lib/audio-chunk-player';
import { useFeatureFlag } from '@/lib/feature-flags';
import {
  toolbarButtonActiveSurface,
  toolbarButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { audioService } from '@/services/audio-service';
import * as m from '@/paraglide/messages';
import {
  useReadAlongPageType,
  useReadAlongSidebarStore,
  useReadAlongViewer,
  useSetHoveredReadingLabel,
} from './read-along-store';

const labelIcons = {
  body: AlignLeft,
  heading: HSquare,
  caption: MediaImage,
  table: Table,
  equation: SigmaFunction,
  footnote: Asterisk,
  endnote: Notes,
  citation: Quote,
  'cross-reference': ShortcutSquare,
  'reference-entry': BookStack,
  'header-footer': AlignVerticalSpacing,
  'page-number': Hashtag,
  artifact: Erase,
} satisfies Record<DocPackReadingClass, typeof AlignLeft>;

const contentClasses: ReadonlyArray<DocPackReadingClass> = [
  'body',
  'heading',
  'caption',
  'table',
  'equation',
];
const notesClasses: ReadonlyArray<DocPackReadingClass> = [
  'footnote',
  'endnote',
  'citation',
  'cross-reference',
  'reference-entry',
];
const pageClasses: ReadonlyArray<DocPackReadingClass> = [
  'header-footer',
  'page-number',
  'artifact',
];
export const developerOnlyClasses: ReadonlySet<DocPackReadingClass> = new Set([
  'body',
  'artifact',
]);

const groupLabelClassName =
  'px-2.5 pt-3 pb-1 text-[11px] font-semibold tracking-[0.08em] text-[var(--v2-text-muted)] uppercase';

const rowClassName =
  'flex cursor-pointer items-center gap-3 rounded-[10px] px-2.5 py-2 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]';

function useReadingLabelsDocument() {
  const audioItem = audioService.store.useCurrentAudio();
  const readingLabels = useFeatureFlag('readingLabels');
  const { isWebAppDoc } = useReadAlongPageType();
  const { viewMode } = useReadAlongMainContentView(
    audioItem ?? null,
    useReadAlongViewer().value,
  );
  const docPack = useDocPack(audioItem).pack;
  if (!readingLabels || !isWebAppDoc || !audioItem || !docPack) return null;
  return {
    audioId: audioItem.id,
    catalog: docPack.catalog,
    pdfView: viewMode === 'pdf',
  };
}

export function ReadingLabelsButton() {
  const readingDocument = useReadingLabelsDocument();
  const showLabelRegions = useDocPackShowLabelRegions();
  const { sidebarState, setSidebarState } = useReadAlongSidebarStore();
  const setHovered = useSetHoveredReadingLabel();

  if (!readingDocument) return null;
  const open = sidebarState === 'reading';

  return (
    <Button
      aria-label={m.reading_labels_title()}
      aria-expanded={open}
      variant='ghost'
      className={cn(toolbarButtonSurface, open && toolbarButtonActiveSurface)}
      onClick={() => {
        setHovered(null);
        setSidebarState(open ? 'closed' : 'reading');
      }}
    >
      <span className='relative'>
        <FilterList aria-hidden className='size-[18px]' />
        {readingDocument.pdfView && showLabelRegions && (
          <span
            aria-hidden
            className='absolute -top-0.5 -right-1 size-[7px] rounded-full bg-[var(--v2-accent)] ring-2 ring-[var(--v2-top-bar-bg)]'
          />
        )}
      </span>
      <span className='max-md:hidden'>{m.reading_labels_title()}</span>
    </Button>
  );
}

export function ReadingLabelsResetButton() {
  const readingDocument = useReadingLabelsDocument();
  if (!readingDocument) return null;
  return (
    <ResetButton
      audioId={readingDocument.audioId}
      catalog={readingDocument.catalog}
    />
  );
}

function ResetButton({
  audioId,
  catalog,
}: {
  audioId: string;
  catalog: DocPackCatalog;
}) {
  const { labels, locked, resetLabels } = useDocPackReadingLabelControls(
    audioId,
    catalog,
  );
  if (locked) return null;
  return (
    <button
      type='button'
      disabled={!labels.some((label) => label.overridden)}
      onClick={resetLabels}
      className='flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--v2-button-radius)] px-2 text-[12px] font-medium text-[var(--v2-text-muted)] transition-colors hover:bg-black/[0.04] hover:text-[var(--v2-text-primary)] disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-white/[0.06]'
    >
      <Undo aria-hidden className='size-3.5' strokeWidth={1.75} />
      {m.common_reset_to_default()}
    </button>
  );
}

export function ReadingLabelsPanel() {
  const readingDocument = useReadingLabelsDocument();
  if (!readingDocument) return null;
  return <ReadingLabelsSettings {...readingDocument} />;
}

function ReadingLabelsSettings({
  audioId,
  catalog,
  pdfView,
}: {
  audioId: string;
  catalog: DocPackCatalog;
  pdfView: boolean;
}) {
  const { labels, locked, setLabelEnabled } = useDocPackReadingLabelControls(
    audioId,
    catalog,
  );
  const developer = clientEnv.mode !== 'production';
  const shown = developer
    ? labels
    : labels.filter((label) => !developerOnlyClasses.has(label.class));
  const inGroup = (classes: ReadonlyArray<DocPackReadingClass>) =>
    shown.filter((label) => classes.includes(label.class));
  const rowProps = { locked, onChange: setLabelEnabled };

  return (
    <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-5'>
      {locked && (
        <p className='px-2.5 pb-1 text-[12px] leading-snug text-[var(--v2-text-muted)]'>
          {m.reading_labels_locked()}
        </p>
      )}
      {pdfView && <ShowOnPageRow />}
      <LabelGroup
        title={m.reading_labels_group_content()}
        labels={inGroup(contentClasses)}
        {...rowProps}
      />
      <LabelGroup
        title={m.reading_labels_group_notes()}
        labels={inGroup(notesClasses)}
        {...rowProps}
      />
      <LabelGroup
        title={m.reading_labels_group_page()}
        labels={inGroup(pageClasses)}
        {...rowProps}
      />
    </div>
  );
}

function ShowOnPageRow() {
  const showLabelRegions = useDocPackShowLabelRegions();
  const { setShowLabelRegions } = useDocPackReadingPreferencesActions();
  return (
    <div className='mt-1'>
      <label className={rowClassName}>
        <Eye
          aria-hidden
          strokeWidth={1.5}
          className='size-4 shrink-0 text-[var(--v2-text-primary)]'
        />
        <span className='flex-1 truncate text-[13px] font-medium text-[var(--v2-text-primary)]'>
          {m.reading_labels_show_on_page()}
        </span>
        <Switch
          checked={showLabelRegions}
          onCheckedChange={setShowLabelRegions}
        />
      </label>
    </div>
  );
}

function LabelGroup({
  title,
  labels,
  locked,
  onChange,
}: {
  title: string;
  labels: ReadonlyArray<DocPackReadingLabel>;
  locked: boolean;
  onChange: (cls: DocPackReadingClass, enabled: boolean) => void;
}) {
  const setHovered = useSetHoveredReadingLabel();
  if (labels.length === 0) return null;
  return (
    <div role='group' aria-label={title}>
      <div aria-hidden className={groupLabelClassName}>
        {title}
      </div>
      {labels.map((label) => {
        const Icon = labelIcons[label.class];
        const preview = () => {
          setHovered(label.class);
        };
        const endPreview = () => {
          setHovered(null);
        };
        return (
          <label
            key={label.class}
            className={cn(
              rowClassName,
              locked && 'cursor-default hover:bg-transparent',
            )}
            onPointerEnter={preview}
            onPointerLeave={endPreview}
          >
            <Icon
              aria-hidden
              strokeWidth={1.5}
              className={cn(
                'size-4 shrink-0 transition-opacity',
                !label.enabled && 'opacity-40',
              )}
              style={{ color: label.color }}
            />
            <span className='flex min-w-0 flex-1 flex-col'>
              <span className='truncate text-[13px] font-medium text-[var(--v2-text-primary)]'>
                {docPackReadingLabelName(m, label.class)}
              </span>
              <span className='truncate text-[12px] text-[var(--v2-text-muted)]'>
                {docPackReadingLabelHint(m, label.class)}
              </span>
            </span>
            <Switch
              checked={label.enabled}
              disabled={locked}
              onFocus={preview}
              onBlur={endPreview}
              onCheckedChange={(enabled) => {
                onChange(label.class, enabled);
              }}
            />
          </label>
        );
      })}
    </div>
  );
}
